<template>
  <div class="section-overview ov">
    <!-- En-tête : accueil, période, actions rapides -->
    <div class="ov-header">
      <div class="ov-hello">
        <h1 class="ov-title">
          {{ greeting }}
          <Tooltip :text="$t('dash.overview.beta_help')" position="bottom"><span class="ov-beta">{{ $t('dash.overview.beta') }}</span></Tooltip>
        </h1>
        <div class="ov-hello-row">
          <p class="ov-subtitle">{{ $t('dash.overview.subtitle_v2') }}</p>
          <DashboardSwitcher :current-id="null" />
        </div>
      </div>

      <div class="ov-actions">
        <Tooltip v-for="action in quickActions" :key="action.key" :text="$t(`dash.overview.help.action.${action.key}`)" position="bottom">
          <button
            class="ov-action"
            :class="{ 'ov-action--primary': action.primary }"
            @click="go(action.target)"
          >
            <i class="material-icons">{{ action.icon }}</i>
            <span>{{ action.label }}</span>
            <span v-if="action.badge" class="ov-action-badge">{{ action.badge > 99 ? '99+' : action.badge }}</span>
          </button>
        </Tooltip>
      </div>
    </div>

    <!-- À traiter : pastilles compactes, détail dépliable -->
    <OverviewAlerts
      :pages="pagesData"
      :risk-posts="riskPosts"
      :validation-posts="validationPosts"
      :assigned-posts="assignedPosts"
      :inbox-count="inboxAllowed ? inboxCount : 0"
      :inbox-events="inboxEvents"
      :inbox-events-loading="inboxEventsLoading"
      @load-inbox="loadInboxEvents"
      @open-inbox-event="openInboxEvent"
      :can-edit="canEdit"
      :can-reconnect-inline="canReconnectInline"
      :loading="!sideLoaded"
      @reconnect="reconnect"
      @go="go"
      @open-post="openPublishedPost"
    />

    <!-- Dernières publications à gauche ; à venir + Inbox empilés à droite, la colonne de gauche prend leur hauteur -->
    <div class="ov-columns ov-columns--feed">
      <OverviewLastPosts
        class="ov-feed-main"
        :last="lastPostsView"
        :last-stats="lastPostStats"
        :loading="!lastPostsView && (lastPostsLoading || overviewLoading)"
        @go="go"
        @open-last-post="openPublishedPost"
        @open-network="openOnNetwork"
      />
      <div class="ov-feed-side">
        <OverviewUpcoming
          :items="upcomingView"
          :loading="upcomingLoading && !upcoming.length"
          :can-edit="canEdit"
          @go="go"
          @open-post="openPublishedPost"
        />
        <OverviewInbox
          v-if="inboxAllowed"
          :events="inboxEvents"
          :count="inboxCount"
          :loading="inboxEventsLoading"
          @load-inbox="loadInboxEvents"
          @open-inbox-event="openInboxEvent"
          @go="go"
        />
      </div>
    </div>

    <!-- Statistiques : le choix de période est dans l'en-tête du bloc qu'il pilote -->
    <div class="ov-stats">
      <div class="ov-stats-head">
        <div class="ov-block-title">
          <i class="material-icons ov-title-icon">show_chart</i>
          <h2>{{ $t('dash.overview.kpi.title') }}</h2>
          <Tooltip :text="presetHelp(period)" position="top">
            <span class="ov-chip-muted">{{ periodLabel }}</span>
          </Tooltip>
        </div>
        <OverviewPeriodBar
          :period="period"
          :loading="overviewLoading"
          :updating="overviewLoading && !!overview"
          @select="selectPeriod"
          @refresh="loadOverview(true)"
        />
      </div>

      <!-- En cas d'erreur, les données en cache restent affichées -->
      <p v-if="overviewError" class="ov-error">
        {{ $t('dash.overview.error') }}
        <button class="ov-arrow-link" @click="loadOverview(true)">{{ $t('dash.overview.retry') }}</button>
      </p>
      <OverviewReportingHint
        v-if="reportingAllowed && overview && !overviewLoading"
        :per-page="overview.per_page"
        :pages="pagesData"
        @open="openReportingForPages"
      />
      <OverviewKpis
        v-if="!overviewError || overview"
        :class="{ 'ov-refreshing': overviewLoading && overview }"
        :data="overview"
        :loading="overviewLoading && !overview"
        :reporting-allowed="reportingAllowed"
        :parts="['tiles', 'chart']"
        :is-day-granularity="period.key === '24h'"
        @go="name => go({ name })"
      />

      <!-- Top publications + meilleurs créneaux : dépendent aussi de la période -->
      <div class="ov-columns ov-columns--wide-left">
        <OverviewTopPosts
          :top="overview ? overview.top_posts : null"
          :loading="overviewLoading && !overview"
          @open-post="openPublishedPost"
        />
        <OverviewBestTimes
          v-if="reportingAllowed"
          :grid="overview ? overview.best_times : null"
          :loading="overviewLoading && !overview"
        />
      </div>

      <OverviewNetworks
        :per-page="overview ? overview.per_page : null"
        :pages="pagesData"
        :loading="overviewLoading && !overview"
      />
    </div>

    <!-- Santé du compte : connexions des pages, sources de contenu, présence locale -->
    <div class="ov-health">
      <div class="ov-block-title ov-section-title">
        <i class="material-icons ov-title-icon">verified_user</i>
        <h2>{{ $t('dash.overview.health_section.title') }}</h2>
      </div>
      <div class="ov-columns ov-columns--equal">
        <OverviewPages
          :pages="pagesData"
          :per-page="overview ? overview.per_page : null"
          :can-edit="canEdit"
          :reporting-allowed="reportingAllowed"
          :can-reconnect-inline="canReconnectInline"
          :inbox-counts="inboxAllowed ? inboxCounts : {}"
          :next-scheduled="nextScheduledByPage"
          @reconnect="reconnect"
          @native-reconnected="onNativeReconnected"
          @go="go"
          @create-post="createPostForPage"
          @open-reporting="openReportingForPage"
          @open-inbox="openInboxForPage"
        />
        <OverviewSources :user-id="cacheUserId()" :can-edit="canEdit" @go="go" @open-post="openPublishedPost" @open-network="openOnNetwork" />
      </div>
      <OverviewPresence v-if="presenceEnabled" :user-id="cacheUserId()" @go="go" />
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import Tooltip from '../tooltip/tooltipInit.vue'
import DataLoaders from '../overview/dataLoaders'
import { readPeriod } from '../overview/cache'
import OverviewPeriodBar from '../overview/OverviewPeriodBar.vue'
import DashboardSwitcher from '../overview/modules/DashboardSwitcher.vue'
import OverviewKpis from '../overview/OverviewKpis.vue'
import OverviewAlerts from '../overview/OverviewAlerts.vue'
import OverviewPages from '../overview/OverviewPages.vue'
import OverviewUpcoming from '../overview/OverviewUpcoming.vue'
import OverviewTopPosts from '../overview/OverviewTopPosts.vue'
import OverviewLastPosts from '../overview/OverviewLastPosts.vue'
import OverviewInbox from '../overview/OverviewInbox.vue'
import OverviewReportingHint from '../overview/OverviewReportingHint.vue'
import OverviewBestTimes from '../overview/OverviewBestTimes.vue'
import OverviewNetworks from '../overview/OverviewNetworks.vue'
import OverviewSources from '../overview/OverviewSources.vue'
import OverviewPresence from '../overview/OverviewPresence.vue'
import { DEFAULT_PERIOD, loadStoredPeriod, storePeriod, presetHelp, periodLabel } from '../overview/periods'

// Paramètres d'URL gérés par Dash.vue (retours OAuth, liens profonds) : il va rediriger, on ne charge rien
const CALLBACK_PARAMS = ['code', 'oauth_token', 'test_callback']

export default {
  name: 'overview-v2',

  components: {
    Tooltip,
    OverviewPeriodBar,
    DashboardSwitcher,
    OverviewKpis,
    OverviewAlerts,
    OverviewPages,
    OverviewUpcoming,
    OverviewTopPosts,
    OverviewLastPosts,
    OverviewInbox,
    OverviewReportingHint,
    OverviewBestTimes,
    OverviewNetworks,
    OverviewSources,
    OverviewPresence
  },

  mixins: [DataLoaders],

  data() {
    const stored = loadStoredPeriod()
    return {
      period: stored || { ...DEFAULT_PERIOD },
      periodWasStored: !!stored
    }
  },

  beforeMount() {
    this.$ga.page(this.$router)
  },

  mounted() {
    // Redirection historique vers les pages (aucun bot, ou connexion LinkedIn en cours) : à garder en premier
    if (this.getUserRole && this.getUserRole != 'Viewer' && (!localStorage.bots || !localStorage.bots.length || localStorage.linkedin_adding_page)) {
      localStorage.setItem('section', 'pages')
      this.$router.push({
        name: 'pages'
      })
      return
    }
    localStorage.setItem('section', 'overview')
    if (this.isRedirectPending()) return
    this.init()
  },

  computed: {
    greeting() {
      const name = this.currentUser && this.currentUser.firstname
      const hour = moment().hour()
      const key = hour < 5 || hour >= 18 ? 'evening' : 'day'
      return `${this.$t(`dash.overview.hello.${key}`)}${name ? `, ${name}` : ''}`
    },

    periodLabel() {
      return periodLabel(this, this.period)
    },

    quickActions() {
      const actions = []
      if (this.canEdit) {
        actions.push({ key: 'create', icon: 'edit', primary: true, label: this.$t('dash.overview.actions.create_post'), target: { name: 'postCreator' } })
      }
      actions.push({ key: 'planner', icon: 'date_range', label: this.$t('dash.overview.actions.planner'), target: { name: 'posts2', tab: 'planner' } })
      if (this.inboxAllowed) {
        actions.push({ key: 'inbox', icon: 'question_answer', label: this.$t('dash.overview.actions.inbox'), target: { name: 'moderation' }, badge: this.inboxCount })
      }
      if (this.reportingAllowed) {
        actions.push({ key: 'reporting', icon: 'insert_chart', label: this.$t('dash.overview.actions.reporting'), target: { name: 'reporting' } })
      }
      if (this.canEdit) {
        actions.push({ key: 'pages', icon: 'add_circle_outline', label: this.$t('dash.overview.actions.add_page'), target: { name: 'pages' } })
      }
      return actions
    }
  },

  methods: {
    presetHelp(preset) {
      return presetHelp(this, preset)
    },

    isRedirectPending() {
      const usp = new URLSearchParams(location.search)
      if (CALLBACK_PARAMS.some(p => usp.has(p))) return true
      if (usp.has('post_id') && usp.has('page_id')) return true
      if (usp.has('moderation') && usp.has('object_id')) return true
      return !!localStorage.getItem('redirect_params_url')
    },

    async init() {
      this.hydrateFromCache()
      // Les KPI ne dépendent pas des pages : lancés tout de suite, en parallèle (recalculés au retour du Reporting)
      this.loadOverview(this.consumeReportingRefresh())
      const pagesLoaded = this.loadPages()
      if (!this.pagesData.length) await pagesLoaded
      if (!this.alive) return
      this.loadPostsAlerts()
      this.loadInboxCount()
      // Bloc Inbox : pages connues ici (au montage du bloc, elles peuvent ne pas l'être encore)
      if (this.inboxAllowed) this.loadInboxEvents()
    },

    selectPeriod(period) {
      this.period = period
      this.periodWasStored = true
      storePeriod(period)
      // Période déjà consultée : affichage immédiat depuis le cache pendant le rechargement
      const cached = readPeriod(this.cacheKey(), period)
      if (cached) this.overview = cached
      this.loadOverview()
    }
  }
}
</script>

<style src="../overview/overview.css"></style>
